import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import ApiService from "@/api/ApiService";
import TransferFeedApi, { TransferAction, TransferEntry, TransferPlace } from "@/api/TransferFeedApi";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";

type Props = { entry: TransferEntry; place: TransferPlace; region: number; user: number; type: "return" | "forward"; close: () => void };

export default function TransferFeedDialog({ entry, place, region, user, type, close }: Props) {
  const today = dayjs().format("YYYY-MM-DD");
  const queryClient = useQueryClient();
  const [targetType, setTargetType] = useState("specialPlace");
  const [project, setProject] = useState<number | null>(null);
  const [specialPlace, setSpecialPlace] = useState<number | null>(null);
  const [recorder, setRecorder] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const projects = useQuery({
    queryKey: ["transferProjects", user, place.region, today],
    queryFn: () => TransferFeedApi.projects(place.region, today),
    enabled: type === "forward" && targetType === "project",
  });
  const places = useQuery<{ id: number; name: string; erfasser: number | null }[]>({
    queryKey: ["transferTargets", user, place.region],
    queryFn: ApiService.getEntitiesWithFilter("specialPlace", { region: ["region", place.region], appTransferTarget: 1 }, "shortInfo"),
    enabled: type === "forward",
  });
  const recorders = useQuery({
    queryKey: ["transferRecorders", user, project, today],
    queryFn: () => TransferFeedApi.recorders(project!, today),
    enabled: type === "forward" && targetType === "project" && !!project,
  });
  const locations = useQuery({
    queryKey: ["transferLocations", user, recorder, today],
    queryFn: () => TransferFeedApi.locations(recorder!, today),
    enabled: type === "forward" && targetType === "recorder" && !!recorder,
  });
  // The existing app helpers suggest a sole recorder/location automatically.
  useEffect(() => {
    const ids = Object.keys(recorders.data ?? {});
    if (targetType === "project" && !recorder && ids.length === 1) setRecorder(Number(ids[0]));
  }, [recorders.data, targetType, recorder]);
  useEffect(() => {
    if (targetType !== "recorder" || project || specialPlace || !locations.data) return;
    const projects = Object.keys(locations.data.project);
    const targetPlaces = Object.keys(locations.data.specialPlace).filter((id) =>
      places.data?.some((target) => target.id === Number(id) && !!target.erfasser));
    if (projects.length + targetPlaces.length !== 1) return;
    if (projects.length) setProject(Number(projects[0]));
    else setSpecialPlace(Number(targetPlaces[0]));
  }, [locations.data, places.data, targetType, project, specialPlace]);
  const mutation = useMutation({
    mutationFn: (data: TransferAction) => TransferFeedApi.act(region, place.id, entry.id, data),
    onSuccess: async () => {
      await Promise.all(["transferFeed", "getDeviceById", "getDeviceByNumber", "getKGP", "getKleingeraete", "getProjectJob", "getTransfers"].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })));
      close();
    },
    onError: () => { queryClient.invalidateQueries({ queryKey: ["transferFeed"] }); },
  });
  const valid = type === "return" || (!!specialPlace && !project) || (!!project && !!recorder && !specialPlace);
  const error = mutation.error as { data?: { error?: string; detail?: string }; status?: number } | null;
  const selectTarget = (value: string) => {
    const [kind, id] = value.split(":");
    setProject(kind === "p" ? Number(id) : null);
    setSpecialPlace(kind === "s" ? Number(id) : null);
  };
  return (
    <Dialog open onClose={mutation.isPending ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle>{type === "return" ? (entry.incoming ? "Transfer ablehnen / zurücksenden" : "Transfer zurückholen") : "Gerät weitertransferieren"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Typography>{entry.resource_number} {entry.resource_name}</Typography>
        {type === "return" ? <Typography>Die Zuordnung vom {dayjs(entry.date).format("DD.MM.YYYY")} wird nach {entry.source_number} {entry.source_name} zurückgenommen.</Typography> : <>
          <Typography variant="body2">Der neue Transfer wird für heute, {dayjs(today).format("DD.MM.YYYY")}, gebucht.</Typography>
          <TextField select label="Transfer-Ziel" value={targetType} disabled={mutation.isPending}
            onChange={(event) => { setTargetType(event.target.value); setProject(null); setSpecialPlace(null); setRecorder(null); }}>
            <MenuItem value="specialPlace">Buchort</MenuItem>
            <MenuItem value="project">Baustelle</MenuItem>
            <MenuItem value="recorder">Kollege</MenuItem>
          </TextField>
          {targetType === "specialPlace" ? <TextField select label="Zielort" value={specialPlace ?? ""} disabled={mutation.isPending || !places.isSuccess}
            onChange={(event) => { setSpecialPlace(Number(event.target.value)); setProject(null); setRecorder(null); }}>
            {(places.data ?? []).filter((target) => !!target.erfasser && target.id !== place.id).map((target) => <MenuItem key={target.id} value={target.id}>{target.name}</MenuItem>)}
          </TextField> : null}
          {targetType === "project" ? <>
            <TextField select label="Baustelle" value={project ?? ""} disabled={mutation.isPending || !projects.isSuccess}
              onChange={(event) => { setProject(Number(event.target.value)); setRecorder(null); }}>
              {(projects.data ?? []).map((target) => <MenuItem key={target.id} value={target.id}>{target.number} {target.name}</MenuItem>)}
            </TextField>
            <TextField select label="Erfasser" value={recorder ?? ""} disabled={mutation.isPending || !recorders.isSuccess}
              onChange={(event) => setRecorder(Number(event.target.value))}>
              {Object.entries(recorders.data ?? {}).map(([id, name]) => <MenuItem key={id} value={Number(id)}>{name}</MenuItem>)}
            </TextField>
            {recorders.isSuccess && !Object.keys(recorders.data).length ? <Alert severity="info">Kein passender Erfasser gefunden. Bitte die Dispo kontaktieren.</Alert> : null}
          </> : null}
          {targetType === "recorder" ? <>
            <ManyToOneSelect entity="staffMember" version="shortInfo" label="Kollege" valueId={recorder ?? 0} disabled={mutation.isPending}
              remoteFilter={{ region: ["region", place.region], baumobilErfasser: 1 }}
              onChange={(_, id) => { setRecorder(id || null); setProject(null); setSpecialPlace(null); }} />
            <TextField select label="Zielort" value={project ? `p:${project}` : specialPlace ? `s:${specialPlace}` : ""}
              disabled={mutation.isPending || !locations.isSuccess} onChange={(event) => selectTarget(event.target.value)}>
              {Object.entries(locations.data?.project ?? {}).map(([id, [name, number]]) => <MenuItem key={`p:${id}`} value={`p:${id}`}>{number} {name}</MenuItem>)}
              {Object.entries(locations.data?.specialPlace ?? {}).filter(([id]) => places.data?.some((target) => target.id === Number(id) && !!target.erfasser))
                .map(([id, name]) => <MenuItem key={`s:${id}`} value={`s:${id}`}>{name}</MenuItem>)}
            </TextField>
          </> : null}
          {[places, projects, recorders, locations].some((query) => query.isError) ? <Alert severity="error">Die Zielauswahl konnte nicht vollständig geladen werden.</Alert> : null}
          <TextField label="Kommentar" multiline minRows={2} value={comment} disabled={mutation.isPending} onChange={(event) => setComment(event.target.value)} />
        </>}
        {mutation.isError ? <Alert severity="error">{error?.data?.error ?? error?.data?.detail ?? "Transfer konnte nicht gespeichert werden. Bitte den Verlauf aktualisieren."}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button disabled={mutation.isPending} onClick={close}>Abbrechen</Button>
        <Button disabled={!valid || mutation.isPending} onClick={() => mutation.mutate({ action: type, project, specialPlace, erfasser: recorder, comment })}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
}
